/* eslint-disable @typescript-eslint/no-explicit-any */
import { clone } from '@jack-henry/frontend-utils/functions';
import { Nullable } from '@treasury/utils';
import { DomainEntity } from '../shared';
import { ReportResult } from './reports.service';

export const nullReportResult: Nullable<ReportResult> = {
    id: 0,
    updatedDate: '',
    updatedBy: '',
    title: '',
    canEmail: true,
    canPrint: true,
    dateType: '',
    reportType: '',
    totalRecords: 0,
    downloadOptions: [],
    data: [],
    sortBy: '',
    isAscending: true,
    totalCheckingBalance: 0,
    totalSavingsBalance: 0,
    checkingTotalCount: 0,
    savingTotalCount: 0,
    totalTransactionCount: 0,
    checkingTotalAmount: 0,
    savingTotalAmount: 0,
    totalTransactionAmount: 0,
};

export enum ReportPageId {
    CurrentDayBalance = 'DayBalanceReport',
    CurrentDayTransaction = 'dayTransactionsReport',
    // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
    PriorDayBalance = 'DayBalanceReport',
    // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
    PriorDayTransaction = 'dayTransactionsReport',
    DateRangeBalance = 'dateRangeBalanceReport',
    DateRangeTransaction = 'dateRangeTransactionsReport',
    Edi = 'EdiReport',
}

export class ReportResultBaseModel extends DomainEntity<Nullable<ReportResult>> {
    constructor(dto = clone(nullReportResult)) {
        super(dto);
        this.dto = dto;
    }

    public createDefault(): this {
        return new ReportResultBaseModel(this.dto) as this;
    }

    public get id() {
        return this.dto.id;
    }

    public get title() {
        return this.dto?.title;
    }

    public get subtitle() {
        return this.dto.title?.split(':').pop()?.trim();
    }

    public get pageId() {
        const title = this.dto.title?.toLowerCase();
        if (title?.includes('current day balance')) {
            return ReportPageId.CurrentDayBalance;
        } else if (title?.includes('current day transaction')) {
            return ReportPageId.CurrentDayTransaction;
        } else if (title?.includes('prior day balance')) {
            return ReportPageId.PriorDayBalance;
        } else if (title?.includes('prior day transaction')) {
            return ReportPageId.PriorDayTransaction;
        } else if (title?.includes('date range balance')) {
            return ReportPageId.DateRangeBalance;
        } else if (title?.includes('date range transaction')) {
            return ReportPageId.DateRangeTransaction;
        } else {
            return ReportPageId.Edi;
        }
    }

    public get canEmail() {
        return this.dto.canEmail;
    }

    public get canPrint() {
        return this.dto.canPrint;
    }

    public get dateType() {
        return this.dto.dateType;
    }

    public get reportType() {
        return this.dto.reportType;
    }

    public get totalRecords() {
        return this.dto.totalRecords;
    }

    public get downloadOptions() {
        return this.dto?.downloadOptions || [];
    }

    public get sortBy() {
        return this.dto.sortBy;
    }

    public get isAscending() {
        return this.dto.isAscending;
    }

    public get data() {
        // group transaction report data for prior/current day reports by account number with shape {accountNumber: string, accountName: string, data: DateRangeTransactionReportItem[]}
        if (this.pageId === ReportPageId.CurrentDayTransaction) {
            const groupedData = this.dto.data.reduce((acc: any, item: any) => {
                const account = acc.find((a: any) => a.accountNumber === item.accountNumber);
                if (account) {
                    account.data.push(item);
                } else {
                    acc.push({
                        accountNumber: item.accountNumber,
                        accountName: item.accountName,
                        accountType: item.accountType,
                        data: [item],
                    });
                }
                return acc;
            }, []);
            return groupedData;
        }

        return this.dto.data;
    }

    // Balance Report Totals
    public get totalCheckingBalance() {
        return this.dto.totalCheckingBalance;
    }

    public get totalCheckingBalanceCount() {
        return this.dto.data.filter((item: any) => item.accountType === 'Checking').length;
    }

    public get totalSavingsBalance() {
        return this.dto.totalSavingsBalance;
    }

    public get totalSavingsBalanceCount() {
        return this.dto.data.filter((item: any) => item.accountType === 'Savings').length;
    }

    public get totalLoanBalance() {
        return this.dto.data
            .filter((item: any) => item.accountType === 'Loan')
            .reduce((acc: number, item: any) => acc + item.currentBalance, 0);
    }

    public get totalLoanBalanceCount() {
        return this.dto.data.filter((item: any) => item.accountType === 'Loan').length;
    }

    // Transaction Report Totals
    public get savingTotalCount() {
        return this.dto.savingTotalCount;
    }

    public get savingsTotals() {
        // filter out the savings account type and sum the debit and credit amounts separately
        const savings = this.dto.data.filter((item: any) => item.accountType === 'Savings');
        const debitTotal = savings.reduce((acc: number, item: any) => acc + item.debitAmount, 0);
        const creditTotal = savings.reduce((acc: number, item: any) => acc + item.creditAmount, 0);
        const debitCount = savings.filter((item: any) => item.debitAmount > 0).length;
        const creditCount = savings.filter((item: any) => item.creditAmount > 0).length;
        return {
            debitTotal,
            creditTotal,
            debitCount,
            creditCount,
        };
    }

    public get checkingTotalCount() {
        return this.dto.checkingTotalCount;
    }

    public get checkingTotals() {
        // filter out the checking account type and sum the debit and credit amounts separately
        const checking = this.dto.data.filter((item: any) => item.accountType === 'Checking');
        const debitTotal = checking.reduce((acc: number, item: any) => acc + item.debitAmount, 0);
        const creditTotal = checking.reduce((acc: number, item: any) => acc + item.creditAmount, 0);
        const debitCount = checking.filter((item: any) => item.debitAmount > 0).length;
        const creditCount = checking.filter((item: any) => item.creditAmount > 0).length;
        return {
            debitTotal,
            creditTotal,
            debitCount,
            creditCount,
        };
    }

    public get totalTransactionCount() {
        return this.dto.totalTransactionCount;
    }

    public get totalTransactions() {
        // sum the debit and credit amounts separately
        const debitTotal = this.dto.data.reduce(
            (acc: number, item: any) => acc + item.debitAmount,
            0
        );
        const creditTotal = this.dto.data.reduce(
            (acc: number, item: any) => acc + item.creditAmount,
            0
        );
        const debitCount = this.dto.data.filter((item: any) => item.debitAmount > 0).length;
        const creditCount = this.dto.data.filter((item: any) => item.creditAmount > 0).length;
        return {
            debitTotal,
            creditTotal,
            debitCount,
            creditCount,
        };
    }

    public get downloadRequestDto() {
        return {
            pageId: this.pageId,
            outputType: '',
            includeHeader: true,
            filterModel: {},
            downloadPdfTypeSelected: 'PDF',
            downloadFormats: [] as string[],
            isPortrait: false,
        };
    }
}
